"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DeleteAgencyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  agencyName?: string;
  isDeleting?: boolean;
}

/**
 * Confirmation before an agency and everything beneath it is destroyed.
 *
 * Two things about the copy are requirements rather than taste, and both come
 * from what the delete actually does now that the endpoint no longer refuses it.
 *
 * The heading NAMES THE AGENCY, and the description SPELLS THE CASCADE OUT in
 * full — its people, its content trips, and every deliverable and asset under
 * those trips, permanently. A generic "Delete this agency?" reads like removing
 * a row, and an operator who did not know the rest is one who finds out
 * afterwards. Together the two lines read as the sentence the story asks for:
 * "Delete <name>? This removes its users, its content trips, and every
 * deliverable and asset under them. This cannot be undone." Split that way
 * rather than repeating the name in both, since Radix announces the title and
 * description as one.
 *
 * This dialog is THE ONLY GATE. Nothing is checked ahead of it — an agency with
 * people and trips deletes exactly like an empty one — so the copy is the whole
 * of the operator's warning.
 */
export function DeleteAgencyDialog({
  open,
  onOpenChange,
  onConfirm,
  agencyName,
  isDeleting = false,
}: DeleteAgencyDialogProps) {
  // The dialog stays mounted between opens, so it renders once with no agency
  // selected; the name must never come out as an empty pair of quotes.
  const name = agencyName?.trim();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {/* `break-words`: an agency name can be long and unbroken, and the
              card is a fixed 32rem. */}
          <AlertDialogTitle className="break-words">
            {name ? `Delete "${name}"?` : "Delete this agency?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This removes its users, its content trips, and every deliverable and
            asset under them. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>No</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting..." : "Yes, delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
